import { useState, useRef } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Shield, Mail, KeyRound, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SuperAdminLogin = ({ onLogin }) => {
  const [step, setStep] = useState("login"); // login | otp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpMethod, setOtpMethod] = useState("email"); // "email" | "totp"
  // pendingEmail: email confermato dal backend (evita bug autofill browser mobile)
  const pendingEmail = useRef("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Leggi il valore direttamente dal DOM per catturare anche l'autofill
    const submittedEmail = e.target.elements[0]?.value || email;
    const submittedPassword = e.target.elements[1]?.value || password;
    try {
      const res = await axios.post(`${API}/superadmin/auth/login`, {
        email: submittedEmail,
        password: submittedPassword,
      });
      
      // Check if direct login (OTP disabled) - response contains access_token
      if (res.data.access_token) {
        localStorage.setItem("sa_token", res.data.access_token);
        toast.success("Accesso SuperAdmin riuscito");
        onLogin(res.data.access_token, res.data.user);
        return;
      }
      
      // OTP required (email or TOTP) — salva l'email confermata dal backend
      pendingEmail.current = res.data.email || submittedEmail;
      const method = res.data.method || "email";
      setOtpMethod(method);
      if (method === "totp") {
        toast.info("Inserisci il codice dall'app authenticator");
      } else {
        toast.success("Codice di verifica inviato alla tua email");
      }
      setStep("otp");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Credenziali non valide");
    } finally { setLoading(false); }
  };

  const handleOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Usa l'email confermata dal backend, non lo state React (bug autofill mobile)
    const emailToSend = pendingEmail.current || email;
    try {
      const res = await axios.post(`${API}/superadmin/auth/verify-otp`, {
        email: emailToSend,
        otp,
      });
      localStorage.setItem("sa_token", res.data.access_token);
      toast.success("Accesso SuperAdmin riuscito");
      onLogin(res.data.access_token, res.data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Codice non valido");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(145deg, #F0EFFE 0%, #F5F5FA 50%, #FFF0F4 100%)" }}>
      <div className="absolute top-0 left-0 w-72 h-72 rounded-full opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, #6C63FF40 0%, transparent 70%)", transform: "translate(-30%, -30%)" }} />
      <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, #FF658440 0%, transparent 70%)", transform: "translate(30%, 30%)" }} />

      <div className="w-full max-w-sm relative z-10 bg-white rounded-[28px] p-8" style={{ boxShadow: "0 8px 40px rgba(108,99,255,0.12), 0 1px 0 rgba(255,255,255,0.8) inset" }}>
        <div className="text-center mb-8">
          <img src="/yollo-logo.png" alt="Yollo" className="w-14 h-14 mx-auto mb-4 rounded-2xl object-contain" />
          <h1 className="text-xl font-black text-gray-900 tracking-tight">Yollo SuperAdmin</h1>
          <p className="text-sm text-gray-400 mt-1">Pannello di gestione piattaforma</p>
        </div>

        {step === "login" ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-gray-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-[52px] rounded-2xl border-0 text-sm font-medium"
                  style={{ backgroundColor: "#F5F5FA", color: "#1A1A1E" }}
                  placeholder="admin@yollo.it" required data-testid="sa-email-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-gray-700">Password</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-[52px] rounded-2xl border-0 text-sm font-medium"
                  style={{ backgroundColor: "#F5F5FA", color: "#1A1A1E" }}
                  placeholder="Password" required data-testid="sa-password-input" />
              </div>
            </div>
            <div className="pt-2">
              <button type="submit" disabled={loading}
                className="w-full h-[52px] flex items-center justify-center gap-2 rounded-2xl text-white font-semibold text-base transition-all active:scale-[0.98] disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #6C63FF, #8B85FF)", boxShadow: "0 4px 16px rgba(108,99,255,0.35)" }}
                data-testid="sa-login-btn">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Accedi"}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleOTP} className="space-y-4">
            <div className="text-center mb-2">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#F0EFFE" }}>
                {otpMethod === "totp" ? (
                  <Smartphone className="h-5 w-5" style={{ color: "#6C63FF" }} />
                ) : (
                  <Mail className="h-5 w-5" style={{ color: "#6C63FF" }} />
                )}
              </div>
              <p className="text-sm text-gray-500">
                {otpMethod === "totp" ? (
                  <>Apri <span className="font-semibold" style={{ color: "#6C63FF" }}>Google Authenticator</span> e inserisci il codice</>
                ) : (
                  <>Codice inviato a <span className="font-semibold" style={{ color: "#6C63FF" }}>{email}</span></>
                )}
              </p>
            </div>
            <div className="space-y-2">
              <Input type="text" value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                className="text-center text-2xl tracking-[0.5em] font-mono h-14 rounded-2xl border-0"
                style={{ backgroundColor: "#F5F5FA", color: "#1A1A1E" }}
                placeholder={otpMethod === "totp" ? "------" : "------"}
                maxLength={8} autoFocus data-testid="sa-otp-input" />
              {otpMethod === "totp" && (
                <p className="text-xs text-center text-gray-400">
                  Puoi anche usare un codice di recupero
                </p>
              )}
            </div>
            <button type="submit" disabled={loading || otp.length < 6}
              className="w-full h-[52px] flex items-center justify-center gap-2 rounded-2xl text-white font-semibold text-base transition-all active:scale-[0.98] disabled:opacity-60"
              style={{ background: "linear-gradient(135deg, #6C63FF, #8B85FF)", boxShadow: "0 4px 16px rgba(108,99,255,0.35)" }}
              data-testid="sa-verify-btn">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Verifica Codice"}
            </button>
            <button type="button" onClick={() => { setStep("login"); setOtp(""); }}
              className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors">
              Torna al login
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default SuperAdminLogin;
